'use client';
/* eslint-disable jsx-a11y/alt-text */
import Image, { type ImageProps } from 'next/image';
import { AiGeneratedBadge } from '@eliadmin/core/src/components/image/ai-generated-badge';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { DeferredImagePicture } from './deferred-image-picture';

export type ImageWrappedProps = ImageProps & {
  preserveAspectRatio?: 'width' | 'height' | 'both';
  /** Modra homepage only: keep offscreen media out of the hero's download queue. */
  deferUntilNearViewport?: boolean;
  deferViewportMedia?: string;
};

const getSrcString = (src: ImageProps['src']) => {
  if (!src) return '';
  if (typeof src === 'string') return src;
  if ('src' in src) return src.src;
  if ('default' in src) return src.default.src;
  return '';
};

const hasAiFlag = (src: ImageProps['src']) => {
  const raw = getSrcString(src);
  if (!raw) return false;

  try {
    const url = new URL(raw, 'http://localhost');
    return url.searchParams.get('ai') === '1';
  } catch {
    return /[?&]ai=1(?:&|$)/.test(raw);
  }
};

const getRoundedClassNames = (className?: string) => {
  if (!className) return undefined;
  const tokens = className.split(/\s+/).filter(Boolean);
  const roundedTokens = tokens.filter(token => token === 'rounded' || token.startsWith('rounded-'));
  return roundedTokens.length ? roundedTokens.join(' ') : undefined;
};

const hasBorderClass = (className?: string) => {
  if (!className) return false;
  return className.split(/\s+/).some(token => token === 'border' || token.startsWith('border-'));
};

const FIXED_SIZE_PATTERN = /^(\d+(?:\.\d+)?)(px|rem)$/;

const parseFixedSlotSize = (value: string) => {
  const match = value.trim().match(FIXED_SIZE_PATTERN);
  if (!match) return null;

  const numericValue = Number(match[1]);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return null;

  return Math.round(match[2] === 'rem' ? numericValue * 16 : numericValue);
};

const parseMaxFixedImageSize = (sizes?: string) => {
  if (!sizes) return null;

  const candidates = sizes
    .split(',')
    .map(part => part.trim())
    .filter(Boolean)
    .map(part => {
      const lastSpaceIndex = part.lastIndexOf(' ');
      const slotSize = lastSpaceIndex === -1 ? part : part.slice(lastSpaceIndex + 1);
      return parseFixedSlotSize(slotSize);
    });

  if (!candidates.length || candidates.some(value => value === null)) {
    return null;
  }

  return Math.max(...candidates.filter((value): value is number => value !== null));
};

const isZeroDimension = (value: ImageProps['width'] | ImageProps['height']) => {
  return value === 0 || value === '0';
};

const getTailwindClass = (token: string) => token.split(':').pop() ?? token;

const isWidthToken = (token: string) => {
  const className = getTailwindClass(token);
  return (
    className === 'w-auto' ||
    className === 'w-full' ||
    className === 'w-screen' ||
    /^w-(?:\d|px|full|screen|\[)/.test(className) ||
    className.startsWith('min-w-') ||
    className.startsWith('max-w-')
  );
};

const isHeightToken = (token: string) => {
  const className = getTailwindClass(token);
  return (
    className === 'h-auto' ||
    className === 'h-full' ||
    className === 'h-screen' ||
    /^h-(?:\d|px|full|screen|\[)/.test(className) ||
    className.startsWith('min-h-') ||
    className.startsWith('max-h-')
  );
};

const isAutoWidthToken = (token: string) => getTailwindClass(token) === 'w-auto';
const isAutoHeightToken = (token: string) => getTailwindClass(token) === 'h-auto';

const getCssControlledDimensions = (className?: string) => {
  const tokens = className?.split(/\s+/).filter(Boolean) ?? [];
  const widthTokens = tokens.filter(isWidthToken);
  const heightTokens = tokens.filter(isHeightToken);
  const widthControlled = widthTokens.length > 0;
  const heightControlled = heightTokens.length > 0;
  const concreteWidthControlled = widthTokens.some(token => !isAutoWidthToken(token));
  const concreteHeightControlled = heightTokens.some(token => !isAutoHeightToken(token));
  const autoWidthControlled = widthTokens.some(isAutoWidthToken);
  const autoHeightControlled = heightTokens.some(isAutoHeightToken);

  return {
    widthControlled,
    heightControlled,
    concreteWidthControlled,
    concreteHeightControlled,
    autoWidthControlled,
    autoHeightControlled,
  };
};

const getAspectRatioMode = (
  props: ImageProps,
  preserveAspectRatio?: ImageWrappedProps['preserveAspectRatio'],
) => {
  if (preserveAspectRatio) return preserveAspectRatio;

  const {
    widthControlled,
    heightControlled,
    concreteWidthControlled,
    concreteHeightControlled,
    autoWidthControlled,
    autoHeightControlled,
  } = getCssControlledDimensions(typeof props.className === 'string' ? props.className : undefined);

  if (concreteWidthControlled && !concreteHeightControlled) return 'height';
  if (concreteHeightControlled && !concreteWidthControlled) return 'width';
  if (concreteWidthControlled && autoHeightControlled) return 'height';
  if (concreteHeightControlled && autoWidthControlled) return 'width';
  if (widthControlled !== heightControlled) return widthControlled ? 'height' : 'width';

  return undefined;
};

const preserveAspectRatioStyle = (
  props: ImageProps,
  preserveAspectRatio?: ImageWrappedProps['preserveAspectRatio'],
) => {
  const aspectRatioMode = getAspectRatioMode(props, preserveAspectRatio);

  if (!aspectRatioMode || props.fill) return props;

  return {
    ...props,
    style: {
      ...props.style,
      ...((aspectRatioMode === 'width' || aspectRatioMode === 'both') && {
        width: 'auto',
      }),
      ...((aspectRatioMode === 'height' || aspectRatioMode === 'both') && {
        height: 'auto',
      }),
    },
  };
};

export const ImageWrapped = (props: ImageWrappedProps) => {
  const { preserveAspectRatio, deferUntilNearViewport, deferViewportMedia, ...baseProps } = props;
  const { fill } = baseProps;
  const showAiBadge = hasAiFlag(baseProps.src);
  const fixedSize = parseMaxFixedImageSize(baseProps.sizes);
  const shouldFallbackToSizes =
    !fill &&
    fixedSize !== null &&
    isZeroDimension(baseProps.width) &&
    isZeroDimension(baseProps.height);

  const imageProps = preserveAspectRatioStyle(
    // Explicit sizes describe the rendered slot, not the intrinsic image dimensions.
    // Keep them for Next's responsive candidate selection, including fixed slots.
    shouldFallbackToSizes
      ? { ...baseProps, width: fixedSize, height: fixedSize, sizes: undefined }
      : baseProps,
    preserveAspectRatio,
  );

  const mayDefer = deferUntilNearViewport && !baseProps.priority && !baseProps.preload &&
    baseProps.loading !== 'eager' && baseProps.fetchPriority !== 'high';
  const image = mayDefer ? (
    <DeferredImagePicture media={deferViewportMedia}>
      <Image {...imageProps} />
    </DeferredImagePicture>
  ) : <Image {...imageProps} />;

  if (!showAiBadge) return image;

  const roundedClassName = getRoundedClassNames(baseProps.className);
  const shouldClip = Boolean(roundedClassName) || hasBorderClass(baseProps.className);
  const wrapperClassName = cn(
    fill ? 'relative block h-full w-full' : 'relative inline-block',
    shouldClip && 'overflow-hidden',
    roundedClassName,
  );

  return (
    <span className={wrapperClassName}>
      {image}
      <AiGeneratedBadge />
    </span>
  );
};

export default ImageWrapped;
